import { buildStoreCartAutoGeneratedDesignGroupDescription } from '../storeCart.constants';
import {
  buildStoreCartDesignGroupMap,
  getSortedStoreCartDesignGroupEntries,
  getStoreCartDesignGroupName,
  type IStoreCartDesignGroupShell,
} from './storeCartDesignGroups.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { ICreateStoreRequest, ICreateV4OrderItem } from '@/common/interfaces/store.interface';

const resolvePrimaryImageSide = (item: ICartItemDetail): string => {
  if (item.frontImage) {
    return 'front';
  }

  if (item.backImage) {
    return 'back';
  }

  const normalizedPrimaryImage = item.primaryImage?.toLowerCase() ?? '';

  if (normalizedPrimaryImage.includes('front')) {
    return 'front';
  }

  if (normalizedPrimaryImage.includes('back')) {
    return 'back';
  }

  return 'front';
};

const buildCreateStoreOrderItem = (item: ICartItemDetail): ICreateV4OrderItem => ({
  design_id: item.designId,
  primary_image: resolvePrimaryImageSide(item),
  description: '',
  product_title: '',
  product_description: '',
  // Send real ids, not mark booleans: greek_licensed expects an org id and
  // collegiate_licensed a school id. The api-server re-derives both from the
  // proof item server-side, so these values act only as a fallback.
  greek_licensed: item.organizationId ?? 0,
  collegiate_licensed: item.collegiateId ?? 0,
  proof_item_id: item.proofItemId,
});

const getOrderedDesignGroupKeys = (
  groupedItems: Record<string, ICartItemDetail[]>,
  designGroupShells: Record<string, IStoreCartDesignGroupShell>
): string[] => {
  const groupedKeys = Object.keys(groupedItems);
  const orderedKeys = getSortedStoreCartDesignGroupEntries(designGroupShells)
    .map(([groupKey]) => groupKey)
    .filter((groupKey) => groupedItems[groupKey]?.length);

  groupedKeys.forEach((groupKey) => {
    if (!orderedKeys.includes(groupKey)) {
      orderedKeys.push(groupKey);
    }
  });

  return orderedKeys;
};

export const buildCreateStorePayloadFromCartItems = (
  cartItems: ICartItemDetail[],
  designGroupShells: Record<string, IStoreCartDesignGroupShell> = {},
  clientId?: number,
  clientName?: string,
  createdById?: number,
  createdByName?: string
): ICreateStoreRequest => {
  const groupedItems = buildStoreCartDesignGroupMap(cartItems);
  const orderedGroupKeys = getOrderedDesignGroupKeys(groupedItems, designGroupShells);

  const designGroups = orderedGroupKeys.map((groupKey, index) => {
    const designGroupName =
      designGroupShells[groupKey]?.designGroupName ?? getStoreCartDesignGroupName(index);

    return {
      name: designGroupName,
      description: buildStoreCartAutoGeneratedDesignGroupDescription(designGroupName),
      design_ids: [
        ...new Set(
          groupedItems[groupKey].map((item) => item.designId).filter((designId) => designId > 0)
        ),
      ],
    };
  });

  const orderItems = orderedGroupKeys.flatMap((groupKey) =>
    groupedItems[groupKey].map(buildCreateStoreOrderItem)
  );

  return {
    isStore: true,
    design_groups: designGroups,
    order_items: orderItems,
    client_name: clientName,
    ...(clientId != null ? { client_id: clientId } : {}),
    ...(createdById != null ? { created_by_id: createdById } : {}),
    ...(createdByName ? { created_by: createdByName } : {}),
  };
};
